// Patch-range builders for rule-level CSS batching.
// They minimize touched rule spans and choose safe patch strategy.

export type CssRulePatchSpan = {
  deleteStart: number
  deleteEndExclusive: number
  insertStart: number
  insertEndExclusive: number
}

// Build one or multiple patch spans based on previous/next rule diff.
export function buildCssRulePatchSpans(
  previousRules: string[],
  nextRules: string[],
  prefix: number,
  suffix: number,
): CssRulePatchSpan[] {
  // Build segmented replacement ranges when list lengths are equal.
  if (previousRules.length === nextRules.length) {
    const segmented = buildEqualLengthReplacementSpans(previousRules, nextRules, prefix, suffix)
    // Return segmented spans when differences were found.
    if (segmented.length > 0) {
      return segmented
    } else {
      // Continue with fallback span build when no segmented diff exists.
    }
  } else {
    // Continue with fallback span build for insert/delete size changes.
  }

  const deleteStart = prefix
  const deleteEndExclusive = previousRules.length - suffix
  const insertEndExclusive = nextRules.length - suffix
  // Return one fallback middle span patch.
  return [{
    deleteStart,
    deleteEndExclusive,
    insertStart: deleteStart,
    insertEndExclusive,
  }]
}

// Decide whether current span set should use rule patch or fallback replace.
export function shouldUseRulePatch(
  previousRuleCount: number,
  nextRuleCount: number,
  spans: CssRulePatchSpan[],
): boolean {
  const operationCount = countRulePatchOperations(spans)
  const baseline = Math.max(previousRuleCount, nextRuleCount, 1)
  const heavyThreshold = Math.max(60, baseline * 2)
  const ratio = operationCount / baseline

  // Skip patch when operation count is too high.
  if (operationCount > heavyThreshold) {
    return false
  } else if (ratio > 1.2) {
    // Skip patch when relative patch ratio is too high.
    return false
  } else {
    // Return true when patch cost is within configured limits.
    return true
  }
}

// Count delete+insert operations needed to apply given spans.
function countRulePatchOperations(spans: CssRulePatchSpan[]): number {
  let operationCount = 0
  // Sum delete and insert operations for each span.
  for (const span of spans) {
    operationCount += (span.deleteEndExclusive - span.deleteStart)
    operationCount += (span.insertEndExclusive - span.insertStart)
  }

  // Return total operation count for strategy checks.
  return operationCount
}

// Build replacement spans for equal-length rule arrays.
function buildEqualLengthReplacementSpans(
  previousRules: string[],
  nextRules: string[],
  prefix: number,
  suffix: number,
): CssRulePatchSpan[] {
  const spans: CssRulePatchSpan[] = []
  const endExclusive = previousRules.length - suffix
  let cursor = prefix

  // Scan middle window and collect contiguous replacement ranges.
  while (cursor < endExclusive) {
    // Skip unchanged rules between changed ranges.
    if (previousRules[cursor] === nextRules[cursor]) {
      cursor += 1
      continue
    } else {
      // Continue changed range capture.
    }

    const start = cursor
    // Consume contiguous changed rules in this segment.
    while (cursor < endExclusive && previousRules[cursor] !== nextRules[cursor]) {
      cursor += 1
    }

    spans.push({
      deleteStart: start,
      deleteEndExclusive: cursor,
      insertStart: start,
      insertEndExclusive: cursor,
    })
  }

  // Return contiguous replacement spans.
  return spans
}
